import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Users, Target } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  KPICard,
  LoadingGrid,
  formatCurrency,
  formatHours,
} from './statisticsShared';
import type { StatisticsOutletContext } from './statisticsShared';
import { useHideAmounts } from '@/hooks/useHideAmounts';

export default function MyStatsTab() {
  const { data, loading, hasFinancialData, labels } = useOutletContext<StatisticsOutletContext>();
  const { hideAmounts, amountClassName } = useHideAmounts();

  const myStats = data?.my_stats;
  const myTargets = myStats?.targets;
  const targetPct = myTargets?.target_progress_percentage ?? 0;

  if (myStats) {
    return (
      <div className="space-y-6">
        {/* Personal KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="My Hours Billed"
            value={formatHours(myStats.summary?.billable_hours)}
            subtitle={`${formatHours(myStats.summary?.total_hours)} total`}
            loading={loading}
          />
          {hasFinancialData && (
            <KPICard
              title="My Revenue"
              value={formatCurrency(myStats.summary?.total_revenue)}
              loading={loading}
            />
          )}
          <KPICard
            title="Days Worked"
            value={String(myStats.summary?.days_worked ?? 0)}
            subtitle={labels.current.toLowerCase()}
            loading={loading}
          />
          {hasFinancialData && (
            <KPICard
              title="My Avg Rate"
              value={`${formatCurrency(myStats.summary?.average_rate)}/h`}
              loading={loading}
            />
          )}
        </div>

        {/* Yearly Target Progress */}
        {myTargets && parseFloat(myTargets.yearly_target) > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-base">Yearly Target Progress</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-baseline">
                  <span className="text-3xl font-bold">
                    {parseFloat(myTargets.ytd_billable_hours).toFixed(0)}h
                  </span>
                  <span className="text-sm text-muted-foreground">
                    of {parseFloat(myTargets.yearly_target).toFixed(0)}h target
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className={cn(
                      'h-3 rounded-full transition-all duration-500',
                      targetPct >= 100 ? 'bg-emerald-500' :
                      targetPct >= 80 ? 'bg-blue-500' :
                      targetPct >= 50 ? 'bg-amber-500' : 'bg-rose-500'
                    )}
                    style={{ width: `${Math.min(targetPct, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span className={cn(
                    'font-medium',
                    parseFloat(myTargets.hours_ahead_behind) >= 0 ? 'text-emerald-600' : 'text-rose-600'
                  )}>
                    {parseFloat(myTargets.hours_ahead_behind) >= 0
                      ? `${parseFloat(myTargets.hours_ahead_behind).toFixed(0)}h ahead of schedule`
                      : `${Math.abs(parseFloat(myTargets.hours_ahead_behind)).toFixed(0)}h behind schedule`
                    }
                  </span>
                  <span className="text-muted-foreground">
                    {targetPct.toFixed(0)}% on track
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* My Top Work */}
        {myStats.top_work && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* My Top Clients */}
            {myStats.top_work.clients && myStats.top_work.clients.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">My Top Clients</CardTitle>
                  <CardDescription>Where I spend the most time</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Client</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                        {hasFinancialData && <TableHead className="text-right">Revenue</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {myStats.top_work.clients.map((c: any, i: number) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium truncate max-w-[180px]">
                            {c.matter__clients__name || 'Unknown'}
                          </TableCell>
                          <TableCell className="text-right">{parseFloat(c.hours || 0).toFixed(0)}h</TableCell>
                          {hasFinancialData && (
                            <TableCell className={`text-right ${amountClassName}`}>{hideAmounts ? '•••' : formatCurrency(c.revenue)}</TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* My Top Matters */}
            {myStats.top_work.matters && myStats.top_work.matters.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">My Top Matters</CardTitle>
                  <CardDescription>What I've been working on</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Matter</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                        {hasFinancialData && <TableHead className="text-right">Revenue</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {myStats.top_work.matters.map((m: any, i: number) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium truncate max-w-[180px]">
                            {m.matter__name || 'Unknown'}
                          </TableCell>
                          <TableCell className="text-right">{parseFloat(m.hours || 0).toFixed(0)}h</TableCell>
                          {hasFinancialData && (
                            <TableCell className={`text-right ${amountClassName}`}>{hideAmounts ? '•••' : formatCurrency(m.revenue)}</TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return <LoadingGrid count={4} />;
  }

  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12">
        <Users className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">Personal Stats</h3>
        <p className="text-muted-foreground text-center max-w-md">
          Your personal performance statistics will appear here once data is available.
        </p>
      </CardContent>
    </Card>
  );
}
